import * as React from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Button, makeStyles, MessageBar, MessageBarBody, Popover, PopoverSurface, PopoverTrigger, tokens } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { useFormikContext } from 'formik'
import useAxios from '../../../../hooks/useAxios'
import Loading from '../../../../components/Loading'
import { SortDirection } from '../../../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    useOverviewStyles,
} from '../../../../pages/BaseOverview/BaseOverview'
import { BaseOverviewFilterType, generateFromDate, SearchHintIcon } from '../../../../pages/BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../../../../pages/BaseOverview/BaseSortMenu'
import { Message } from '@microsoft/microsoft-graph-types'
import { EntityDto } from '../../../../config/appConfig'
import FileEmailListItem from './FileEmailListItem'
import { ViewFileProps } from '../ViewFile'
import { Destination, IGraphArrayPayload } from '../../../../context/AxiosContext'
import { extractReferenceNumbersFromMessage, FILE_REFERENCE_NUMBER_PROP } from '../../../../helpers/referenceNumberHelper'
import BaseHeaderItem from '../../../../pages/BaseOverview/BaseHeaderItem'
import useUser from '../../../../hooks/useUser'
import HammockDialog, { useDialogStyles } from '../../../components/Dialog/HammockDialog'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import PreviewMessageDialogBody, { PreviewMessageDialogBodyProps } from '../../../components/Dialog/Message/PreviewMessageDialogBody'
import FileEmailHeaderActionsButtons, { EmailHeaderActionsOverviewProps } from './FileEmailHeaderActionsButtons'
import FileEmailHeaderFilterTags, { EmailHeaderFilterTagsOverviewProps } from './FileEmailHeaderFilterTags'
import { useTranslation } from 'react-i18next'
import DateRange from '../../../../components/DatePicker/DateRange'

export const useItemStyles = makeStyles({
    listEmailItem: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewIconWidth) auto var(--baseOverviewDateWidth)',
    },
    listEmailItemSelected: {
        backgroundColor: `${tokens.colorBrandBackground2Pressed}!important`,
    },
    listEmailDetails: {
        display: 'grid',
        gridTemplateColumns: '1fr`',
        gridTemplateRows: '1fr 1fr 1fr`',
    },
})

export type ExtendedEmailOverviewDto = Omit<Message, 'id'> &
    EntityDto & {
        inbound?: boolean
        url?: string //Custom URL with which the item can be downloaded.
    }

export type EmailOverviewProps = BaseOverviewProps<
    ExtendedEmailOverviewDto,
    EmailFilterType,
    EmailSortingType,
    BaseOverviewCount,
    EmailHeaderActionsOverviewProps,
    EmailHeaderFilterTagsOverviewProps
> &
    ViewFileProps

export type EmailLineOverviewProps = EmailOverviewProps &
    BaseOverviewLineItemProps<ExtendedEmailOverviewDto, EmailFilterType, EmailSortingType> & {
        onPreviewClick: (message: ExtendedEmailOverviewDto) => void
    }

export type EmailFilterType = BaseOverviewFilterType & {
    from?: string
    to?: string
}

export type EmailSorting = {
    sentDateTime: string
}

export type EmailSortingType = BaseOverviewSortingType<EmailSorting>

function EmailFilter() {
    const { values, setFieldValue } = useFormikContext<EmailFilterType>()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.OutlookNewWide) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
        </>
    )
}

function FilterInputHint() {
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    return (
        <Popover open={open} onOpenChange={(_e, data) => setOpen(data.open)} withArrow>
            <PopoverTrigger disableButtonEnhancement>
                <Button size={'small'} appearance={'transparent'} icon={<SearchHintIcon />} />
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
                <h3>{translate('file.pivot.email.search.hint.title')}</h3>
                <br />
                <div dangerouslySetInnerHTML={{ __html: translate('file.pivot.email.search.hint.body') }}></div>
            </PopoverSurface>
        </Popover>
    )
}

const headerSortingItems: EmailSortingType[] = [
    { property: ['sentDateTime'], direction: SortDirection.Ascending, descriptionKey: 'sorting.dateasc' },
    { property: ['sentDateTime'], direction: SortDirection.Descending, default: true, descriptionKey: 'sorting.datedesc' },
]

export default function FileEmailOverview(props: Partial<EmailOverviewProps> & ViewFileProps) {
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const dialogStyles = useDialogStyles()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { currentMailbox } = useUser()
    const { t: translate } = useTranslation()

    const overviewApiRef = useRef<{
        onUpdatePageItems: (column: number, page: number, start: number, size: number) => Promise<ExtendedEmailOverviewDto[]>
    }>()
    const [reloadTrigger, setReloadTrigger] = useState<number>(0)
    const reload = () => {
        setReloadTrigger(reloadTrigger + 1)
    }

    const [emailReplicationFolderId, setEmailReplicationFolderId] = useState<string | null>()
    const [emailUserFolderId, setEmailUserFolderId] = useState<string>()

    const [count, setCount] = useState<BaseOverviewCount>({ total: 0 })
    const [items, setItems] = useState<ExtendedEmailOverviewDto[]>([])
    const [selectedItems, setSelectedItems] = useState<ExtendedEmailOverviewDto[]>([])
    const [openPreviewMessage, setOpenPreviewMessage] = useState<ExtendedEmailOverviewDto>()

    useEffect(() => {
        axios
            .get('/email/replication/parent-folder/id', { params: { references: [props.file.reference] } })
            .then((response: AxiosResponse<{ [key: string]: string }>) => setEmailReplicationFolderId(response.data[props.file.reference] ?? null))
    }, [props.file])

    useEffect(() => {
        axios
            .get(`/email/${currentMailbox}/folder/${props.file.reference}`, {
                baseURL: Destination.Hammock,
                headers: { SuppressErrors: 'true' },
            })
            .then((response: AxiosResponse<string>) => setEmailUserFolderId(response.data))
    }, [props.file])

    const defaultFilterValues = useMemo<EmailFilterType>(
        () => ({
            from: '',
            to: '',
            query: '',
            tag: 'all',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const countItems = useCallback(
        (_column?: number, filterValues?: EmailFilterType): Promise<BaseOverviewCount> => {
            return axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select: 'id',
                        $filter: filterValues.query
                            ? undefined
                            : [
                                  `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and contains(ep/value, '[${props.file.reference}]'))`,
                                  filterValues.from && `sentDateTime ge ${filterValues.from}`,
                                  filterValues.to && `sentDateTime le ${filterValues.to}`,
                              ]
                                  .filter(Boolean)
                                  .join(' and '),
                        $top: filterValues.query ? 1000 : undefined,
                        $count: true,
                        $search: filterValues.query ? `"${filterValues.query} ${props.file.reference}"` : undefined,
                    },
                })
                .then((response: AxiosResponse<IGraphArrayPayload<Message>>): BaseOverviewCount => ({
                    total: response.data['@odata.count'] ?? response.data.value.length ?? 0,
                }))
        },
        [props.file, emailReplicationFolderId],
    )

    const loadItems = useCallback(
        (
            _column?: number,
            filterValues?: EmailFilterType,
            sortingValues?: EmailSortingType,
            start?: number,
            size?: number,
        ): Promise<ExtendedEmailOverviewDto[]> => {
            return axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select:
                            'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                        $filter: filterValues.query
                            ? undefined
                            : [
                                  `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and contains(ep/value, '[${props.file.reference}]'))`,
                                  filterValues.from && `sentDateTime ge ${filterValues.from}`,
                                  filterValues.to && `sentDateTime le ${filterValues.to}`,
                              ]
                                  .filter(Boolean)
                                  .join(' and '),
                        $expand: filterValues.query ? undefined : `singleValueExtendedProperties($filter=id eq '${FILE_REFERENCE_NUMBER_PROP}')`,
                        $top: `${size}`,
                        $skip: filterValues.query ? undefined : `${start}`,
                        $orderby: filterValues.query ? undefined : `${sortingValues.property[0]} ${sortingValues.direction.toLowerCase()}`,
                        $search: filterValues.query ? `"${filterValues.query} ${props.file.reference}"` : undefined,
                    },
                })
                .then((response: AxiosResponse<IGraphArrayPayload<Message>>): ExtendedEmailOverviewDto[] =>
                    (response.data.value || []).map((item: Message) => ({
                        id: item.id,
                        ...item,
                        references: extractReferenceNumbersFromMessage(item),
                        inbound:
                            item.sender?.emailAddress.address
                                .substring(item.sender?.emailAddress.address.indexOf('@'))
                                .localeCompare(currentMailbox?.substring(currentMailbox?.indexOf('@'))) !== 0,
                        url: `${process.env.INBOXER_HOST}/api/download-message?hostKey=${currentHost?.RowKey}&email=${encodeURIComponent(currentHost.ReplicationMail)}&messageId=${item.id}`,
                    })),
                )
        },
        [props.file, emailReplicationFolderId],
    )

    if (emailReplicationFolderId === undefined) {
        return <Loading />
    }
    if (emailReplicationFolderId === null) {
        return (
            <MessageBar intent={'error'}>
                <MessageBarBody>{translate('file.pivot.email.error.no_folder')}</MessageBarBody>
            </MessageBar>
        )
    }

    return (
        <>
            <HammockDialog<PreviewMessageDialogBodyProps>
                open={openPreviewMessage !== undefined}
                setOpen={(open: boolean) => setOpenPreviewMessage(open ? openPreviewMessage : undefined)}
                dialogBody={PreviewMessageDialogBody}
                surfaceProps={{
                    className: dialogStyles.dialogHeight,
                }}
                dialogBodyProps={() => ({
                    index: items.indexOf(openPreviewMessage),
                    message: openPreviewMessage,
                    destination: Destination.ReplicationEmail,
                    hasPrevious: items.indexOf(openPreviewMessage) > 0,
                    hasNext: count.total > items.indexOf(openPreviewMessage) + 1,
                    previous: () => setOpenPreviewMessage(items[items.indexOf(openPreviewMessage) - 1]),
                    next: () => {
                        ;(items.length > items.indexOf(openPreviewMessage) + 1
                            ? Promise.resolve(items)
                            : overviewApiRef.current?.onUpdatePageItems?.(0, undefined, items.length, undefined)
                        ).then(_items => {
                            setOpenPreviewMessage(_items[_items.indexOf(openPreviewMessage) + 1])
                        })
                    },
                })}
            />

            <BaseOverview<
                ExtendedEmailOverviewDto,
                EmailFilterType,
                EmailSortingType,
                BaseOverviewCount,
                EmailHeaderActionsOverviewProps,
                EmailHeaderFilterTagsOverviewProps
            >
                defaultFilterValues={defaultFilterValues}
                countItems={countItems}
                loadItems={loadItems}
                onCountItems={(_column, result) => setCount(result)}
                onLoadItems={(_column, _columnPages, items) => setItems([...items])}
                listItemHeaderContent={BaseHeaderItem}
                listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
                listItemContent={(props: EmailLineOverviewProps) => (
                    <FileEmailListItem {...props} onPreviewClick={item => setOpenPreviewMessage(item)} />
                )}
                listItemClassnames={props => [
                    itemStyles.listEmailItem,
                    props.item && selectedItems.includes(props.item) && itemStyles.listEmailItemSelected,
                ]}
                listColumnFullWidth
                headerActionsButtons={FileEmailHeaderActionsButtons}
                headerActionsButtonsProps={{
                    emailUserFolderId: emailUserFolderId,
                    setEmailUserFolderId: setEmailUserFolderId,
                    reload: () => {
                        setSelectedItems([])
                        reload()
                    },
                }}
                headerDefaultFilterTags={FileEmailHeaderFilterTags}
                headerDefaultFilterTagsProps={{
                    selectedItems: selectedItems,
                    clearSelection: () => setSelectedItems([]),
                    reload: () => {
                        setSelectedItems([])
                        reload()
                    },
                }}
                headerSortingItems={headerSortingItems}
                filterContent={EmailFilter}
                filterInputHint={FilterInputHint}
                filterFromJson={values => ({
                    ...values,
                    from: values.tag ? generateFromDate(values.tag) : values.from,
                    // to: values.tag ? generateToDate(values.tag) : values.to,
                })}
                filterToJson={values => ({
                    ...values,
                    from: values.tag ? '' : values.from,
                    // to: values.tag ? '' : values.to,
                    icon: undefined,
                    title: undefined,
                })}
                inputPlaceholderKey={'email.search.placeholder'}
                localStorageKeyPrefix={'HammockFileEmailOverview'}
                apiRef={overviewApiRef}
                reloadTrigger={reloadTrigger}
                isItemSelectable={() => true}
                isItemSelected={item => selectedItems.includes(item)}
                onItemSelected={(data, item) => {
                    if (data.checked === true) {
                        setSelectedItems(prevItems => [...new Set([...prevItems, item])])
                    } else {
                        setSelectedItems(prevItems => [...new Set([...prevItems.filter(prevItem => prevItem.id !== item.id)])])
                    }
                }}
                onItemClicked={props => {
                    if (selectedItems.includes(props.item)) {
                        setSelectedItems(prevItems => [...new Set([...prevItems.filter(prevItem => prevItem.id !== props.item.id)])])
                    } else {
                        setSelectedItems(prevItems => [...new Set([...prevItems, props.item])])
                    }
                }}
                {...props}
            />
        </>
    )
}
